import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import Header from "components/Header/Header"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import React, { useEffect, useRef, useState } from "react"
import { Col, Container, Row } from "react-bootstrap"
import {
  Backdrop,
  Grid,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material"
import styled from "@emotion/styled"
import { useDispatch, useSelector } from "react-redux"
import { formatDate, getFormattedDate, toTitleCase } from "helpers/functions"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import TableNoRecords from "components/TableNoRecords"
import { Formik } from "formik"
import CreateGiftCard from "./CreateGiftCard"
import AppButton from "components/StyledComponents/AppButton"
import { validationSchemaGiftCard } from "helpers/validationSchema"
import { superAdminAction } from "redux/superAdmin/slice/user"
import {
  EditGiftCardAction,
  addGiftCardAction,
  deleteGiftCardAction,
  getGiftCardListAction,
  getGiftCardsAction
} from "redux/superAdmin/actions/user"
import { appActions } from "redux/app/slice/app"
import AppPagination from "components/StyledComponents/AppPagination"
import moment from "moment"

const CustomTable = styled(Table)(() => ({
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "relative",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px"
}))

const GiftCard = () => {
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(false)
  const theme = useTheme()
  const [openModal, setopenModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const dispatch = useDispatch()
  const openMenu = Boolean(anchorEl)
  const rowId = useRef(null)
  const { giftCardList, giftCardData, giftCardImageId } = useSelector(
    (store) => store?.superAdmin?.giftCards || []
  )
  const { card_type, image_url, header, start_date, end_date } = giftCardData || {}
  const giftCard_type = useSelector((store) => store?.app?.filters?.giftCard_type || {})
  const [currentPage, setCurrentPage] = useState(1)

  const handleDeleteGiftCard = () => {
    dispatch(deleteGiftCardAction(rowId.current))
    rowId.current = null
    dispatch(appActions.setFilters({ giftCard_type: "All" }))
  }
  const toggleDeletePopup = () => {
    setIsDeletePopupOpen(!isDeletePopupOpen)
  }

  const initialValuesEdit = {
    uploadThumbnail: image_url
      ? {
          id: "",
          url: image_url ?? ""
        }
      : "",
    giftCardType: card_type || "MONTHLY",
    month: moment(start_date) || moment(new Date()),
    header: header || "",
    end_date: moment(end_date) || moment(new Date())
  }

  const initialValues = {
    uploadThumbnail: "",
    giftCardType: "MONTHLY",
    month: moment(new Date()),
    header: "",
    end_date: moment(new Date()).add(3, "months")
  }

  const onOptionsClick = (e, id) => {
    e.stopPropagation()
    rowId.current = id
    setAnchorEl(e.currentTarget)
    dispatch(superAdminAction.setGiftCardID(id))
    dispatch(getGiftCardsAction())
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  function handleAddGiftCard() {
    setIsEdit(false)
    setopenModal(true)
  }

  function handleEditGiftCard() {
    setIsEdit(true)
    setopenModal(true)
  }

  function onClose() {
    setIsEdit(false)
    setopenModal(false)
    dispatch(superAdminAction.setGiftCardID(null))
    dispatch(superAdminAction?.setGiftCardImageId(""))
    dispatch(superAdminAction?.setGiftCardData({}))
  }

  function handleSubmit(e) {
    var date = new Date(e?.month)
    var end_date = new Date(e?.end_date)
    var firstDay =
      e.giftCardType === "MONTHLY" ? new Date(date.getFullYear(), date.getMonth(), 1) : date
    var lastDay =
      e.giftCardType === "MONTHLY" ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : end_date
    const payload = {
      card_type: e.giftCardType || "MONTHLY",
      header: e.header || "",
      start_date: formatDate(firstDay),
      end_date: formatDate(lastDay),
      ...(giftCardImageId ? { image: giftCardImageId } : e.uploadThumbnail ? {} : { image: "" })
    }
    if (isEdit !== false) {
      dispatch(EditGiftCardAction({ payload: payload, onClose: onClose }))
    } else {
      dispatch(addGiftCardAction({ payload: payload, onClose: onClose }))
    }
    dispatch(appActions.setFilters({ giftCard_type: "All" }))
  }

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs())
    document.title = "Montly & Quarterly Price"
    dispatch(getGiftCardListAction({ page: currentPage, giftCard_type: giftCard_type }))
  }, [currentPage, giftCard_type])

  useEffect(() => {
    setCurrentPage(1)
  }, [giftCard_type])

  useEffect(() => {
    dispatch(appActions.initFilters())
    dispatch(appActions.setFilters({ giftCard_type: "All" }))
  }, [])

  return (
    <Container fluid className="p-0  ">
      <DeleteConfirmationDialog
        open={isDeletePopupOpen}
        title="Delete Monthly & Quarterly Prices"
        onDelete={handleDeleteGiftCard}
        onClose={toggleDeletePopup}
        description="Are you sure you want to delete this Price?"
      />
      <Grid container>
        <Grid item xs={12}>
          <Header title="Monthly & Quarterly Prices" breadCrumbs={breadCrumbs}>
            <AppButton onClick={() => handleAddGiftCard()} sx={{ padding: "13px 36px" }}>
              Create Price
            </AppButton>
          </Header>
        </Grid>
      </Grid>
      <Row>
        <Col lg="12">
          <TableContainer>
            <CustomTable stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={
                        index === 0
                          ? { minWidth: column.minWidth, paddingLeft: "24px" }
                          : { minWidth: column.minWidth }
                      }>
                      <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                        {column.label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {giftCardList?.data?.length ? (
                  <>
                    {giftCardList?.data?.map((row, i) => (
                      <TableRow key={row?.id}>
                        <TableCell align="left" style={{ paddingLeft: "24px" }}>
                          <Typography variant="body2Regular">{i + 1}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2Regular">
                            {row?.card_type ? toTitleCase(row?.card_type) : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2Regular">
                            {row?.start_date ? getFormattedDate(row.start_date) : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <span role="button" onClick={(e) => onOptionsClick(e, row.id)}>
                            <MoreHorizIcon
                              sx={() => ({
                                height: "24px",
                                width: "24px"
                              })}
                            />
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <TableNoRecords colSpan={4} />
                )}
              </TableBody>
            </CustomTable>
          </TableContainer>
          <Backdrop sx={{ color: "#fff", zIndex: 1300 }} open={openMenu} onClick={handleMenuClose}>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              PaperProps={{
                sx: {
                  marginTop: "15px",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "12px",
                  "& .MuiList-root": {
                    backgroundColor: theme.palette.background.default,
                    width: "225px",
                    boxSizing: "border-box",
                    padding: "10px 0"
                  }
                }
              }}>
              <StyledMenuItem onClick={() => handleEditGiftCard()}>
                <Typography variant="body1Regular">Edit</Typography>
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => toggleDeletePopup()}
                sx={{
                  borderTop: `2px solid ${theme.palette.background.secondary}`
                }}>
                <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
                  Delete
                </Typography>
              </StyledMenuItem>
            </Menu>
          </Backdrop>
        </Col>
      </Row>
      {giftCardList?.data?.length ? (
        <AppPagination
          onPageChange={setCurrentPage}
          totalPages={giftCardList?.totalPages}
          currentPage={currentPage}
        />
      ) : (
        ""
      )}
      {openModal && (
        <Formik
          onSubmit={handleSubmit}
          validationSchema={validationSchemaGiftCard}
          initialValues={isEdit ? initialValuesEdit : initialValues}>
          {(formik) => (
            <CreateGiftCard
              isEdit={isEdit}
              onClose={onClose}
              formik={formik}
              open={openModal}
              giftCardData={giftCardData}
            />
          )}
        </Formik>
      )}
    </Container>
  )
}

export default GiftCard

const columns = [
  {
    id: "ID",
    label: "ID",
    align: "left",
    minWidth: 50
  },
  {
    id: "Type",
    label: "Type",
    align: "left",
    minWidth: 200
  },
  {
    id: "Month",
    label: "Month",
    align: "left",
    minWidth: 200
  },
  {
    id: "Action",
    label: "Action",
    align: "center",
    minWidth: 70
  }
]
